import { Filter } from "helpers/parseFilter";
import { APP_ROUTES } from "config";
import { produce } from "immer";
import _ from "lodash";
import { DOCUMENT_VERSION_COLUMN } from "pages/Documents/Documents";
import { VIEW_DOCUMENT_COLUMN, WORKFLOWS_PROGRESS_COLUMN } from "pages/Workflows/Workflows";
import { Dispatch } from "react";
import { Store } from "reducers/rootReducer";

/* STATE */

export enum CustomRowCellColumns {
  "DOCUMENTS_VERSION" = DOCUMENT_VERSION_COLUMN,
  "WORKFLOWS_PROGRESS" = WORKFLOWS_PROGRESS_COLUMN,
  "VIEW_DOCUMENT" = VIEW_DOCUMENT_COLUMN,
}

export type CustomRowCellData = { [key in CustomRowCellColumns]?: any };

export enum ActionTypes {
  "CREATE" = "CREATE",
  "UPDATE" = "UPDATE",
}

export type OriginFormData = {
  pathName: string;
  data: any;
  actionType: ActionTypes;
  id: number | null;
};

// export type OnCancelRedirectFunction = (...args: any[]) => void; // Any number of funcion arguments
export type OnCancelRedirectFunction = () => void; // Any number of funcion arguments

export type ExtraPayloadItems = {
  [moduleName: string]: {
    relatedModuleName: string;
    param: { [paramName: string]: any };
    idResponseFieldName: string;
  };
};

export type AutoSelectAllValuesFields = {
  [moduleName: string]: { fieldName: string; dataSourceFieldName: string }[];
};

export type RelatedFormSuccessResponseIds = {
  [moduleName: string]: {
    [relatedFormFieldName: string]: {
      id: number;
      extraDataToDisplay?: string;
    }[];
  };
  // [moduleName: string]: { [relatedFormFieldName: string]: number };
};

export type RelatedFormIdsToDelete = {
  [moduleName: string]: { [fieldName: string]: number[] }[];
};

export type RelatedFormExtraDataToDisplayFields = {
  [originModuleName: string]: {
    [relatedModuleName: string]: { data: string; dataSource: string }[];
  };
};

export type RelatedFormExtraDataToDisplay = {
  [moduleName: string]: { [fieldName: string]: string };
};

type State = {
  closeRightMenu: boolean;
  customRowData: CustomRowCellData | null;
  relatedFormActionType: ActionTypes | null;
  relatedFormData: any;
  relatedFormModuleName: string | null;
  formReadOnlyFields: { [key: string]: string } | null;
  relatedFormDestinationModuleName: string | null;
  relatedFormSuccessResponseIds: RelatedFormSuccessResponseIds | null;
  originFormData: OriginFormData | null;
  relatedFormExtraPayloadItems: ExtraPayloadItems | null;
  relatedFormOriginFormAutoSelectAllValuesFields: AutoSelectAllValuesFields | null;
  relatedFormIdsToDelete: RelatedFormIdsToDelete | null;
  relatedFormExtraDataToDisplay: RelatedFormExtraDataToDisplay | null;
  parentId: number | null;
  programmesId: number | null;
  redirectUrl: string | null;
  redirectUrlDestinationModule: string | null;
  checkForAccessToken: Date | null;
  previousProgrammeId: number | null;
  reloadProjectManagementProgrammeSelectorToggle: boolean | null;
  listPageFilters: Filter[] | null;
};

const initialState: State = {
  closeRightMenu: false,
  customRowData: null,
  relatedFormActionType: null,
  relatedFormData: null,
  relatedFormModuleName: null,
  formReadOnlyFields: null,
  relatedFormDestinationModuleName: null,
  relatedFormSuccessResponseIds: null,
  originFormData: null,
  relatedFormExtraPayloadItems: null,
  relatedFormOriginFormAutoSelectAllValuesFields: null,
  relatedFormIdsToDelete: null,
  relatedFormExtraDataToDisplay: null,
  parentId: null,
  programmesId: null,
  redirectUrl: null,
  redirectUrlDestinationModule: null,
  checkForAccessToken: null,
  previousProgrammeId: null,
  reloadProjectManagementProgrammeSelectorToggle: null,
  listPageFilters: null,
};

/* ACTIONS */
const SET_PREVIOUSPROGRAMMESID = "app/listpage/SET_PREVIOUSPROGRAMMESID";
const SET_PARENTID = "app/listpage/SET_PARENTID";
const CLOSE_RIGHT_MENU = "app/listPage/CLOSE_RIGHT_MENU";
const RESET_CLOSE_RIGHT_MENU = "app/listPage/CLOSE_RIGHT_MENU";
const SET_CUSTOM_ROW_DATA = "app/listPage/SET_CUSTOM_ROW_DATA";
const SET_RELATED_FORM_ACTION_TYPE = "app/listPage/SET_RELATED_FORM_ACTION_TYPE";
const SET_RELATED_FORM_DATA = "app/listPage/SET_RELATED_FORM_DATA";
const SET_RELATED_FORM_MODULE_NAME = "app/listPage/SET_RELATED_FORM_MODULE_NAME";
const SET_FORM_READ_ONLY_FIELDS = "app/listPage/SET_FORM_READ_ONLY_FIELDS";
const SET_RELATED_FORM_DESTINATION_MODULE_NAME =
  "app/listPage/SET_RELATED_FORM_DESTINATION_MODULE_NAME";
const UPDATE_RELATED_FORM_SUCCESS_RESPONSE_IDS =
  "app/listPage/UPDATE_RELATED_FORM_SUCCESS_RESPONSE_IDS";
const CLEAR_RELATED_FORM_SUCCESS_RESPONSE_IDS =
  "app/listPage/CLEAR_RELATED_FORM_SUCCESS_RESPONSE_IDS";
const SET_ORIGIN_FORM_DATA = "app/listPage/SET_ORIGIN_FORM_DATA";
const SET_RELATED_FORM_EXTRA_PAYLOAD_ITEMS = "app/listPage/SET_RELATED_FORM_EXTRA_PAYLOAD_ITEMS";
const SET_RELATED_FORM_ORIGIN_FORM_AUTO_SELECT_ALL_VALUES_FIELDS =
  "app/listPage/SET_RELATED_FORM_ORIGIN_FORM_AUTO_SELECT_ALL_VALUES_FIELDS";
const SET_RELATED_FORM_IDS_TO_DELETE = "app/listPage/SET_RELATED_FORM_IDS_TO_DELETE";
const SET_RELATED_FORM_EXTRA_DATA_TO_DISPLAY =
  "app/listPage/SET_RELATED_FORM_EXTRA_DATA_TO_DISPLAY";
const SET_PROGRAMMES_ID = "app/listpage/SET_PROGRAMMES_ID";
const RESET_PROGRAMMES_ID = "app/listpage/RESET_PROGRAMMES_ID";
const RESET_REDIRECT_URL = "app/listpage/RESET_REDIRECT_URL";
const SET_REDIRECT_URL = "app/listpage/SET_REDIRECT_URL";
const SET_REDIRECT_URL_DESTINATION_MODULE = "app/listpage/SET_REDIRECT_URL_DESTINATION_MODULE";
const RESET_REDIRECT_URL_DESTINATION_MODULE = "app/listpage/RESET_REDIRECT_URL_DESTINATION_MODULE";
const CHECK_FOR_ACCESS_TOKEN_CHANGE = "app/listpage/CHECK_FOR_ACCESS_TOKEN_CHANGE";
const RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE =
  "app/listpage/RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE";
const RESET_RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE =
  "app/listpage/RESET_RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE";
const SET_LIST_PAGE_FILTERS = "app/listpage/SET_LIST_PAGE_FILTERS";

type Action =
  | {
      type: typeof CLOSE_RIGHT_MENU;
      payload: boolean;
    }
  | { type: typeof SET_PARENTID; payload: number | null }
  | { type: typeof SET_PREVIOUSPROGRAMMESID; payload: number | null }
  | { type: typeof RESET_CLOSE_RIGHT_MENU; payload: boolean }
  | { type: typeof SET_CUSTOM_ROW_DATA; payload: CustomRowCellData }
  | { type: typeof SET_RELATED_FORM_ACTION_TYPE; payload: ActionTypes | null }
  | { type: typeof SET_RELATED_FORM_DATA; payload: any }
  | { type: typeof SET_RELATED_FORM_MODULE_NAME; payload: string | null }
  | {
      type: typeof SET_FORM_READ_ONLY_FIELDS;
      payload: { [key: string]: string } | null;
    }
  | {
      type: typeof SET_RELATED_FORM_DESTINATION_MODULE_NAME;
      payload: string | null;
    }
  | {
      type: typeof UPDATE_RELATED_FORM_SUCCESS_RESPONSE_IDS;
      payload: RelatedFormSuccessResponseIds | null;
    }
  | {
      type: typeof CLEAR_RELATED_FORM_SUCCESS_RESPONSE_IDS;
    }
  | { type: typeof SET_ORIGIN_FORM_DATA; payload: OriginFormData | null }
  | {
      type: typeof SET_RELATED_FORM_EXTRA_PAYLOAD_ITEMS;
      payload: ExtraPayloadItems | null;
    }
  | {
      type: typeof SET_RELATED_FORM_ORIGIN_FORM_AUTO_SELECT_ALL_VALUES_FIELDS;
      payload: AutoSelectAllValuesFields | null;
    }
  | {
      type: typeof SET_RELATED_FORM_IDS_TO_DELETE;
      payload: RelatedFormIdsToDelete | null;
    }
  | {
      type: typeof SET_RELATED_FORM_EXTRA_DATA_TO_DISPLAY;
      payload: RelatedFormExtraDataToDisplay | null;
    }
  | { type: typeof SET_PROGRAMMES_ID; payload: number }
  | { type: typeof RESET_PROGRAMMES_ID }
  | { type: typeof SET_REDIRECT_URL; payload: string }
  | { type: typeof SET_REDIRECT_URL_DESTINATION_MODULE; payload: string }
  | { type: typeof RESET_REDIRECT_URL }
  | { type: typeof RESET_REDIRECT_URL_DESTINATION_MODULE }
  | { type: typeof CHECK_FOR_ACCESS_TOKEN_CHANGE; payload: Date | null }
  | { type: typeof RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE }
  | { type: typeof RESET_RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE }
  | { type: typeof SET_LIST_PAGE_FILTERS; payload: Filter[] | null };

/* REDUCER */

export default function reducer(state = initialState, action: Action): State {
  return produce(state, (draft) => {
    switch (action.type) {
      case CLOSE_RIGHT_MENU:
        draft.closeRightMenu = action.payload;
        break;
      case RESET_CLOSE_RIGHT_MENU:
        draft.closeRightMenu = action.payload;
        break;
      case SET_CUSTOM_ROW_DATA:
        draft.customRowData = action.payload;
        break;
      case SET_RELATED_FORM_ACTION_TYPE:
        draft.relatedFormActionType = action.payload;
        break;
      case SET_RELATED_FORM_DATA:
        draft.relatedFormData = action.payload;
        break;
      case SET_RELATED_FORM_MODULE_NAME:
        draft.relatedFormModuleName = action.payload;
        break;
      case SET_FORM_READ_ONLY_FIELDS:
        draft.formReadOnlyFields = action.payload;
        break;
      case SET_RELATED_FORM_DESTINATION_MODULE_NAME:
        draft.relatedFormDestinationModuleName = action.payload;
        break;
      case UPDATE_RELATED_FORM_SUCCESS_RESPONSE_IDS:
        let tempIds: RelatedFormSuccessResponseIds | null = state.relatedFormSuccessResponseIds
          ? { ..._.cloneDeep(state.relatedFormSuccessResponseIds) }
          : null;

        if (tempIds && action.payload) {
          Object.keys(action.payload).forEach((moduleName) => {
            if (action.payload) {
              // typescript warning that it may not exist even though the if statement above checks for this

              if (tempIds && tempIds.hasOwnProperty(moduleName)) {
                const existingModuleValues = tempIds[moduleName];
                const newModuleValues = action.payload[moduleName];

                const finalModuleValues = { ...existingModuleValues };

                Object.keys(newModuleValues).forEach((fieldName) => {
                  let newData = [...newModuleValues[fieldName]];
                  if (existingModuleValues.hasOwnProperty(fieldName)) {
                    newData = [...existingModuleValues[fieldName], ...newData];
                  }
                  finalModuleValues[fieldName] = newData;
                });

                tempIds[moduleName] = finalModuleValues;
              }
            }
          });
        } else if (!tempIds) {
          tempIds = action.payload;
        }

        draft.relatedFormSuccessResponseIds = tempIds;

        // draft.relatedFormSuccessResponseIds = action.payload;

        break;

      case CLEAR_RELATED_FORM_SUCCESS_RESPONSE_IDS:
        draft.relatedFormSuccessResponseIds = null;
        break;
      case SET_ORIGIN_FORM_DATA:
        draft.originFormData = action.payload;
        break;
      case SET_RELATED_FORM_EXTRA_PAYLOAD_ITEMS:
        draft.relatedFormExtraPayloadItems = action.payload;
        break;
      case SET_RELATED_FORM_ORIGIN_FORM_AUTO_SELECT_ALL_VALUES_FIELDS:
        const existingRelatedFormOriginFormAutoSelectAllValuesFields =
          state.relatedFormOriginFormAutoSelectAllValuesFields
            ? _.cloneDeep(state.relatedFormOriginFormAutoSelectAllValuesFields)
            : null;

        const relatedFormOriginFormAutoSelectAllValuesFields = action.payload
          ? _.cloneDeep(action.payload)
          : null;

        let newRelatedFormOriginFormAutoSelectAllValuesFields =
          existingRelatedFormOriginFormAutoSelectAllValuesFields
            ? _.cloneDeep(existingRelatedFormOriginFormAutoSelectAllValuesFields)
            : null;

        if (
          existingRelatedFormOriginFormAutoSelectAllValuesFields &&
          relatedFormOriginFormAutoSelectAllValuesFields
        ) {
          Object.keys(relatedFormOriginFormAutoSelectAllValuesFields).forEach((moduleName) => {
            const moduleValues = relatedFormOriginFormAutoSelectAllValuesFields[moduleName];

            if (
              moduleValues &&
              newRelatedFormOriginFormAutoSelectAllValuesFields &&
              existingRelatedFormOriginFormAutoSelectAllValuesFields &&
              existingRelatedFormOriginFormAutoSelectAllValuesFields.hasOwnProperty(moduleName)
            ) {
              newRelatedFormOriginFormAutoSelectAllValuesFields[moduleName] = [
                ...existingRelatedFormOriginFormAutoSelectAllValuesFields[moduleName],
                ...moduleValues,
              ];
            } else if (moduleValues && newRelatedFormOriginFormAutoSelectAllValuesFields) {
              newRelatedFormOriginFormAutoSelectAllValuesFields[moduleName] = moduleValues;
            }
          });
        } else {
          newRelatedFormOriginFormAutoSelectAllValuesFields =
            relatedFormOriginFormAutoSelectAllValuesFields;
        }

        draft.relatedFormOriginFormAutoSelectAllValuesFields =
          newRelatedFormOriginFormAutoSelectAllValuesFields;

        break;
      case SET_RELATED_FORM_IDS_TO_DELETE:
        draft.relatedFormIdsToDelete = action.payload;
        break;
      case SET_RELATED_FORM_EXTRA_DATA_TO_DISPLAY:
        draft.relatedFormExtraDataToDisplay = action.payload;
        break;
      case SET_PARENTID:
        draft.parentId = action.payload;
        break;
      case SET_PREVIOUSPROGRAMMESID:
        draft.previousProgrammeId = action.payload;
        break;
      case SET_PROGRAMMES_ID:
        draft.programmesId = action.payload;
        break;
      case RESET_PROGRAMMES_ID:
        draft.programmesId = null;
        break;
      case SET_REDIRECT_URL:
        draft.redirectUrl = action.payload;
        break;
      case RESET_REDIRECT_URL:
        draft.redirectUrl = null;
        break;
      case SET_REDIRECT_URL_DESTINATION_MODULE:
        draft.redirectUrlDestinationModule = action.payload;
        break;
      case RESET_REDIRECT_URL_DESTINATION_MODULE:
        draft.redirectUrlDestinationModule = null;
        break;
      case CHECK_FOR_ACCESS_TOKEN_CHANGE:
        draft.checkForAccessToken = action.payload;
        // draft.reloadTable = !draft.reloadTable;
        break;
      case RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE:
        if (draft.reloadProjectManagementProgrammeSelectorToggle === null) {
          draft.reloadProjectManagementProgrammeSelectorToggle = true;
        }
        break;
      case RESET_RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE:
        draft.reloadProjectManagementProgrammeSelectorToggle = null;

        break;
      case SET_LIST_PAGE_FILTERS:
        draft.listPageFilters = action.payload;
        break;
      default:
        break;
    }
  });
}

/* ACTION CREATORS */

export const setCloseRightMenu = (): Action => ({
  type: CLOSE_RIGHT_MENU,
  payload: true,
});

export const setResetCloseRightMenu = (): Action => ({
  type: RESET_CLOSE_RIGHT_MENU,
  payload: false,
});

export const setCustomRowData = (payload: CustomRowCellData): Action => ({
  type: SET_CUSTOM_ROW_DATA,
  payload: payload,
});

export const setRelatedFormActionType = (payload: ActionTypes | null): Action => ({
  type: SET_RELATED_FORM_ACTION_TYPE,
  payload: payload,
});

export const setRelatedFormData = (payload: any): Action => ({
  type: SET_RELATED_FORM_DATA,
  payload: payload,
});

export const setRelatedFormModuleName = (payload: string | null): Action => ({
  type: SET_RELATED_FORM_MODULE_NAME,
  payload: payload,
});

export const setFormReadOnlyFields = (payload: { [key: string]: string } | null): Action => ({
  type: SET_FORM_READ_ONLY_FIELDS,
  payload: payload,
});

export const setRelatedFormDestinationModuleName = (payload: string | null): Action => ({
  type: SET_RELATED_FORM_DESTINATION_MODULE_NAME,
  payload: payload,
});

export const updateRelatedFormSuccessResponseIds = (
  payload: RelatedFormSuccessResponseIds | null
): Action => ({
  type: UPDATE_RELATED_FORM_SUCCESS_RESPONSE_IDS,
  payload: payload,
});

export const clearRelatedFormSuccessResponseIds = (): Action => ({
  type: CLEAR_RELATED_FORM_SUCCESS_RESPONSE_IDS,
});

export const setOriginFormData = (payload: OriginFormData | null): Action => ({
  type: SET_ORIGIN_FORM_DATA,
  payload: payload,
});

export const setRelatedFormExtraPayloadItems = (payload: ExtraPayloadItems | null): Action => ({
  type: SET_RELATED_FORM_EXTRA_PAYLOAD_ITEMS,
  payload: payload,
});

export const setRelatedFormOriginFormAutoSelectAllValuesFields = (
  payload: AutoSelectAllValuesFields | null
): Action => ({
  type: SET_RELATED_FORM_ORIGIN_FORM_AUTO_SELECT_ALL_VALUES_FIELDS,
  payload: payload,
});

export const setRelatedFormIdsToDelete = (payload: RelatedFormIdsToDelete | null): Action => ({
  type: SET_RELATED_FORM_IDS_TO_DELETE,
  payload: payload,
});

export const setRelatedFormExtraDataToDisplay = (
  payload: RelatedFormExtraDataToDisplay | null
): Action => ({
  type: SET_RELATED_FORM_EXTRA_DATA_TO_DISPLAY,
  payload: payload,
});

export const setParentId = (payload: number | null): Action => ({
  type: SET_PARENTID,
  payload: payload,
});

export const setPreviousProgrammeId = (payload: number | null): Action => ({
  type: SET_PREVIOUSPROGRAMMESID,
  payload: payload,
});

export const setProgrammeId = (payload: number): Action => ({
  type: SET_PROGRAMMES_ID,
  payload: payload,
});
export const resetProgrammeId = (): Action => ({
  type: RESET_PROGRAMMES_ID,
});
export const setRedirectUrl = (payload: string): Action => ({
  type: SET_REDIRECT_URL,
  payload: payload,
});
export const setRedirectUrlDestinationModule = (payload: string): Action => ({
  type: SET_REDIRECT_URL_DESTINATION_MODULE,
  payload: payload,
});
export const resetRedirectUrl = (): Action => ({
  type: RESET_REDIRECT_URL,
});
export const resetRedirectUrlDestinationModule = (): Action => ({
  type: RESET_REDIRECT_URL_DESTINATION_MODULE,
});

export const setCheckForAccessTokenChange = (payload: Date | null): Action => ({
  type: CHECK_FOR_ACCESS_TOKEN_CHANGE,
  payload: payload,
});

export const setReloadProjectManagementProgrammeSelectorToggle = (): Action => ({
  type: RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE,
});

export const resetReloadProjectManagementProgrammeSelectorToggle = (): Action => ({
  type: RESET_RELOAD_PROJECT_MANAGEMNET_PROGRAMME_SELECTOR_TOGGLE,
});

export const setListPageFilters = (payload: Filter[] | null): Action => ({
  type: SET_LIST_PAGE_FILTERS,
  payload: payload,
});

/* SELECTORS */

export const selectParentId = (app: Store) => app.listPage.parentId;

export const selectPreviousProgrammeId = (app: Store) => app.listPage.previousProgrammeId;

export const selectCloseRightMenu = (app: Store) => app.listPage.closeRightMenu;

export const selectCustomRowData = (app: Store) => app.listPage.customRowData;

export const selectRelatedFormActionType = (app: Store) => app.listPage.relatedFormActionType;

export const selectRelatedFormData = (app: Store) => app.listPage.relatedFormData;

export const selectRelatedFormModuleName = (app: Store) => app.listPage.relatedFormModuleName;

export const selectFormReadOnlyFields = (app: Store) => app.listPage.formReadOnlyFields;

export const selectRelatedFormDestinationModuleName = (app: Store) =>
  app.listPage.relatedFormDestinationModuleName;

export const selectRelatedFormSuccessResponseIds = (app: Store) =>
  app.listPage.relatedFormSuccessResponseIds;

export const selectOriginFormData = (app: Store) => app.listPage.originFormData;

export const selectRelatedFormExtraPayloadItems = (app: Store) =>
  app.listPage.relatedFormExtraPayloadItems;

export const selectRelatedFormOriginFormAutoSelectAllValuesFields = (app: Store) =>
  app.listPage.relatedFormOriginFormAutoSelectAllValuesFields;

export const selectRelatedFormsIdsToDelete = (app: Store) => app.listPage.relatedFormIdsToDelete;

export const selectRelatedFormsExtraDataToDisplay = (app: Store) =>
  app.listPage.relatedFormExtraDataToDisplay;

export const selectCheckForAccessTokenChange = (app: Store) => app.listPage.checkForAccessToken;

export const updateRelatedFormReduxData = (
  relatedFormData: any,
  relatedFormActionType: ActionTypes | null,
  relatedFormModuleName: string | null,
  dispatch: Dispatch<any>
) => {
  dispatch(setRelatedFormData(relatedFormData));
  dispatch(setRelatedFormActionType(relatedFormActionType));
  dispatch(setRelatedFormModuleName(relatedFormModuleName));
};

export const resetRelatedFormReduxData = (
  dispatch: Dispatch<any>,
  additionalFunction?: () => void
) => {
  dispatch(setRelatedFormData(null));
  dispatch(setRelatedFormActionType(null));
  dispatch(setRelatedFormModuleName(null));
  dispatch(setFormReadOnlyFields(null));
  dispatch(clearRelatedFormSuccessResponseIds());
  dispatch(setOriginFormData(null));
  dispatch(setRelatedFormExtraPayloadItems(null));
  dispatch(setRelatedFormOriginFormAutoSelectAllValuesFields(null));
  dispatch(setRelatedFormExtraDataToDisplay(null));

  if (additionalFunction) {
    additionalFunction();
  }
};

export const selectProgrammeId = (app: Store) => app.listPage.programmesId;

export const selectRedirectUrl = (app: Store) => app.listPage.redirectUrl;

export const selectRedirectUrlDestinationModule = (app: Store) =>
  app.listPage.redirectUrlDestinationModule;

export const selectReloadProjectManagementProgrammeSelectorToggle = (app: Store) =>
  app.listPage.reloadProjectManagementProgrammeSelectorToggle;

export const selectListPageFilters = (app: Store) => app.listPage.listPageFilters;
